import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaPaintBrush,
  FaLaptopCode,
  FaMobileAlt,
  FaCloud,
  FaDatabase,
  FaRobot,
  FaShieldAlt,
  FaChartLine,
  FaCogs,
  FaVrCardboard,
  FaBusinessTime,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { onAuthStateChanged, getAuth, User } from "firebase/auth";
import { db } from "../firebase";
import { ScrollAnimation, StaggerContainer, StaggerItem, ParallaxScroll } from "./ScrollAnimation";

const icons = {
  FaPaintBrush,
  FaLaptopCode,
  FaMobileAlt,
  FaCloud,
  FaDatabase,
  FaRobot,
  FaShieldAlt,
  FaChartLine,
  FaCogs,
  FaVrCardboard,
  FaBusinessTime,
};

type IconKey = keyof typeof icons;

interface Service {
  id: string;
  title: string;
  description: string;
  color: string;
  icon: IconKey;
}

const colorMap: Record<string, { gradient: string; shadow: string; bg: string }> = {
  "bg-blue-500": { gradient: "from-blue-500 to-blue-600", shadow: "shadow-blue-500/25", bg: "bg-blue-50 dark:bg-blue-950/30" },
  "bg-green-500": { gradient: "from-green-500 to-green-600", shadow: "shadow-green-500/25", bg: "bg-green-50 dark:bg-green-950/30" },
  "bg-purple-500": { gradient: "from-purple-500 to-purple-600", shadow: "shadow-purple-500/25", bg: "bg-purple-50 dark:bg-purple-950/30" },
  "bg-red-500": { gradient: "from-red-500 to-red-600", shadow: "shadow-red-500/25", bg: "bg-red-50 dark:bg-red-950/30" },
  "bg-yellow-500": { gradient: "from-yellow-500 to-yellow-600", shadow: "shadow-yellow-500/25", bg: "bg-yellow-50 dark:bg-yellow-950/30" },
};

const ServicesSection: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [formData, setFormData] = useState<Service>({
    id: "",
    title: "",
    description: "",
    color: "bg-blue-500",
    icon: "FaPaintBrush",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchServices = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "services"));
      const fetchedServices: Service[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || "",
          description: data.description || "",
          color: data.color || "bg-blue-500",
          icon: data.icon || "FaPaintBrush",
        };
      });
      setServices(fetchedServices);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { id, ...data } = formData;
    try {
      if (id) {
        await updateDoc(doc(db, "services", id), data);
      } else {
        await addDoc(collection(db, "services"), data);
      }
      fetchServices();
      resetForm();
    } catch (error) {
      console.error("Error saving service:", error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, "services", id));
      fetchServices();
    } catch (error) {
      console.error("Error deleting service:", error);
    }
  };

  const resetForm = () => {
    setFormData({ id: "", title: "", description: "", color: "bg-blue-500", icon: "FaPaintBrush" });
    setIsEditing(false);
  };

  const getColorConfig = (color: string) => {
    return colorMap[color] || { gradient: "from-primary-500 to-primary-600", shadow: "shadow-primary-500/25", bg: "bg-primary-50 dark:bg-primary-950/30" };
  };

  return (
    <section className="section-padding relative" id="services">
      {/* Background decoration with parallax */}
      <ParallaxScroll offset={-40}>
        <div className="absolute top-0 left-1/2 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </ParallaxScroll>

      <div className="max-w-6xl mx-auto relative">
        {/* Section Header with enhanced animation */}
        <ScrollAnimation variant="slideUp" delay={0.1} duration={0.7}>
          <div className="text-center mb-16">
            <motion.div
              className="section-badge"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              What I Do
            </motion.div>
            <h2 className="section-title">
              What I <span className="gradient-text">Offer</span>
            </h2>
            <p className="section-subtitle">
              Comprehensive services tailored to bring your vision to life
            </p>
          </div>
        </ScrollAnimation>

        {/* Services Grid with stagger animation */}
        <StaggerContainer staggerDelay={0.12}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.length ? (
              services.map((service) => {
                const colorConfig = getColorConfig(service.color);
                return (
                  <StaggerItem key={service.id}>
                    <motion.div
                      className="glass-card p-8 group relative overflow-hidden"
                      whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    >
                      {/* Hover gradient background */}
                      <div className={`absolute inset-0 ${colorConfig.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                      {/* Top accent line */}
                      <motion.div
                        className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${colorConfig.gradient} origin-left`}
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      />

                      <div className="relative z-10">
                        {/* Icon */}
                        <motion.div
                          className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorConfig.gradient} flex items-center justify-center text-white text-2xl mb-6 shadow-lg ${colorConfig.shadow}`}
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          {React.createElement(icons[service.icon])}
                        </motion.div>

                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                          {service.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                          {service.description}
                        </p>

                        {/* Decorative arrow on hover */}
                        <div className="mt-6 flex items-center gap-2 text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <span className={`bg-gradient-to-r ${colorConfig.gradient} bg-clip-text text-transparent`}>
                            Learn more
                          </span>
                          <svg className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </div>
                      </div>

                      {/* Admin Actions - Mobile-friendly */}
                      {user && (
                        <div className="absolute top-4 right-4 z-20">
                          <div className="admin-controls">
                            <motion.button
                              className="btn-icon-sm"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                setFormData(service);
                                setIsEditing(true);
                              }}
                              aria-label={`Edit ${service.title} service`}
                            >
                              <FaEdit size={14} />
                            </motion.button>
                            <motion.button
                              className="btn-icon-sm btn-danger"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDelete(service.id)}
                              aria-label={`Delete ${service.title} service`}
                            >
                              <FaTrash size={14} />
                            </motion.button>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </StaggerItem>
                );
              })
            ) : (
              <p className="col-span-full text-center text-gray-400 dark:text-gray-600 py-16">
                No services available yet.
              </p>
            )}
          </div>
        </StaggerContainer>

        {/* Add Button */}
        {user && (
          <motion.div className="flex justify-center mt-10">
            <motion.button
              className="btn-primary"
              onClick={() => setIsEditing(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Add New Service
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Modal */}
      {isEditing && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={resetForm}
        >
          <motion.div
            className="modal-content"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              {formData.id ? "Edit Service" : "Add Service"}
            </h3>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Title</label>
                <input
                  type="text"
                  placeholder="Service title"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Description</label>
                <textarea
                  placeholder="Describe the service"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  className="input-field min-h-[100px]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Color</label>
                <select
                  value={formData.color}
                  onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                  className="input-field"
                >
                  <option value="bg-blue-500">Blue</option>
                  <option value="bg-green-500">Green</option>
                  <option value="bg-purple-500">Purple</option>
                  <option value="bg-red-500">Red</option>
                  <option value="bg-yellow-500">Yellow</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Icon</label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData((prev) => ({ ...prev, icon: e.target.value as IconKey }))}
                  className="input-field"
                >
                  {Object.keys(icons).map((key) => (
                    <option key={key} value={key}>
                      {key.replace("Fa", "").replace(/([A-Z])/g, " $1").trim()}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" className="btn-secondary !py-2.5 !px-5 !text-sm" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary !py-2.5 !px-5 !text-sm">
                  Save
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default ServicesSection;
